import moment from 'moment'
import _ from 'lodash'
import dbCodes from '../../server/dbCodes.js'
import Countries from './Countries.js'
import {RRule} from 'rrule'
import {formatPrice, parsePrice} from '../../utils/price.js'

import CustomerStore from '../stores/CustomerStore.js'

import languages from 'server/languages.js'
import {getLocale} from 'src/helpers/Languages.js'
import Colors from '@utils/Colors.js'
import {getOrderFeesText} from '@utils/orderFees.js'
import {formatDuration} from '@utils/utils.js'
import {getDeliveryZoneNames} from './deliveryZones.js'
import {getPriceTableName} from './priceTables.js'
import {byColumns, filter} from './queryFilter.js'

// Debtor code the customer is invoiced on, multiple customers can share one invoice debtor code
export function invoiceDebtorCode(customer) {
    if (!customer) return ''
    return (customer.useOtherInvoiceDebtorCode ? customer.invoiceDebtorCode : customer.debtorCode) || ''
}

export default {
    orders: (orders, routes, queries, startTime, endTime, startInclude, endInclude, reseller, vehicles, partners) => {
        const filteredOrders = {}

        Object.keys(orders).map((id) => {
            const order = structuredClone(orders[id])
            order.createdTime = moment(new Date(parseInt(order._id.substring(0, 8), 16) * 1000)).format('HH:mm')

            order.branchName = reseller?.settings.branches?.find((branch) => branch.id === order.branchId)?.name || ''

            order.customer = `${order.senderAddress?.name || ''}${order.senderAddress?.name ? ` (${order.customerAddress?.name})` : order.customerAddress?.name}`

            const {status, subStatus} = Colors.orderStatusColor(order)
            order.status = `${subStatus && subStatus !== dbCodes.status[dbCodes.status.geannuleerd()] ? subStatus : status}${Colors.orderRetourSuffix(order)}`
            order.checked = order.checked ? 'Ja' : 'Nee'
            order.invoiced = order.dontInvoice ? 'n.v.t.' : order.invoiced ? 'Ja' : 'Nee'
            order.date = moment(order.date).format('DD-MM-YYYY')
            order.invoiceDate = order.invoiceDate ? moment(order.invoiceDate).format('DD-MM-YYYY') : ''
            order.fees = getOrderFeesText(order, reseller)
            order.carrierName = order.handledBy === 'partner' ? (partners?.[order.carrierId]?.name || '') : (reseller?.settings?.accountName || reseller?.name || '')
            // The carrier columns (logo and name) are filtered on the carrier's name
            order.carrierLogo = order.carrierName
            order.carrierId = order.carrierName
            order.priceTableId = getPriceTableName(reseller?.settings?.orders?.prices, order.priceTableId)

            order.startTime = ''
            order.endTime = ''

            order.address = ''
            order.addressType = ''
            order.addressName = ''
            order.addressStreet = ''
            order.addressPostalCode = ''
            order.addressCity = ''
            order.addressInstructions = ''
            order.addressDeliveryZone = ''
            order.options = ''
            order.addressesAmount = order.addresses.length
            order.attachments = order.addresses
                .flatMap((address) => address.attachments || [])
                .map((attachment) => attachment.fileName || '')
                .filter((fileName) => !!fileName)
                .join(', ')

            order.addresses.map((address) => {
                order.startTime += `${address.startTime} `
                order.endTime += `${address.endTime} `

                order.address += `${address.name} ${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`

                if (address.type === 'pickup') {
                    order.addressType += 'ophaal'
                }

                if (!order.pickupAddress && address.type === 'pickup') {
                    order.pickupAddress = `${address.name} ${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`
                }

                if (address.type === 'delivery') {
                    order.deliveryAddress = `${address.name} ${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`
                    order.addressType += 'bezorg'
                }

                order.addressName += address.name
                order.addressStreet += address.street
                order.addressPostalCode += address.postalCode
                order.addressCity += address.city
                order.addressInstructions += address.instructions || ''
                order.addressDeliveryZone += getDeliveryZoneNames(reseller, address.deliveryZoneId).join('')

                Object.entries(dbCodes.pickupAndDeliveryOptions).forEach(([key, value]) => {
                    if (key.startsWith('minimumAge')) {
                        if (address.minimumAge && key === `minimumAge +${address.minimumAge}`) {
                            order.options += `${value}, `
                        }
                    } else {
                        if (address[key]) {
                            order.options += `${value}, `
                        }
                    }
                })

                if (reseller && address.customOptions?.length > 0) {
                    address.customOptions.forEach((optionId) => {
                        const option = reseller.settings.orders.customOptions?.find((option) => option.id === optionId)

                        if (option) {
                            order.options += `${option.name}, `
                        }
                    })
                }
            })

            order.routeName = routes[order.routeId]?.name || ''
            order.messengerName = (order.routeId ? routes[order.routeId]?.messengerName : order.messenger) || '' // order.messenger is DEPRECATED

            const route = routes[order.routeId]
            const vehicle = vehicles?.find((vehicle) => vehicle._id === route?.vehicleId)
            const vehicleType = reseller?.settings.vehicleTypes?.find((vehicleType) => vehicleType._id === route?.vehicleTypeId)
            const vehicleName = vehicle?.name || vehicleType?.name || ''
            order.vehicleId = vehicleName || ''
            order.vehicleType = vehicleType?.name || ''

            order.barcodes = order.colli?.map((collo) => collo.barcode || '').join(', ') || ''

            order.colli = `${order.colli.length} ${order.colli?.length === 1 ? 'collo' : 'colli'}`

            delete order._id
            delete order.reseller

            const isInTime = (!startInclude && !endInclude) || _.some(order.addresses, (address) => {
                if (startInclude && endInclude) {
                    return address.startTime < endTime && (startTime === '00:00' ? address.endTime >= startTime : address.endTime > startTime)
                }

                if (!startInclude && !endInclude) {
                    return address.startTime >= startTime && address.endTime <= endTime
                }

                if (!startInclude) {
                    return address.startTime >= startTime
                }

                if (!endInclude) {
                    return address.endTime <= endTime
                }
            })

            if (isInTime) {
                if (filter(order, queries)) {
                    filteredOrders[id] = orders[id]
                }
            }
        })

        return filteredOrders
    },

    routes: (routes, queries, reseller, vehicles) => {
        const filteredRoutes = {}

        Object.keys(routes).forEach((id) => {
            const route = structuredClone(routes[id])

            route.routeName = route.name
            route.branchName = reseller?.settings.branches?.find((branch) => branch.id === route.branchId)?.name || ''

            const vehicle = vehicles?.find((vehicle) => vehicle._id === route?.vehicleId)
            const vehicleType = reseller?.settings.vehicleTypes?.find((vehicleType) => vehicleType._id === route?.vehicleTypeId)
            const vehicleName = vehicle?.name || vehicleType?.name || ''
            route.vehicleId = vehicleName || ''
            route.vehicleType = vehicleType?.name || ''

            route.address = ''
            route.addressName = ''
            route.addressStreet = ''
            route.addressPostalCode = ''
            route.addressCity = ''

            route.address += `${route.startPoint.name} ${route.startPoint.street} ${route.startPoint.nr}${route.startPoint.addition || ''} ${route.startPoint.postalCode} ${route.startPoint.city}`
            route.addressStreet += route.startPoint.street
            route.addressPostalCode += route.startPoint.postalCode
            route.addressCity += route.startPoint.city

            route.address += `${route.endPoint.name} ${route.endPoint.street} ${route.endPoint.nr}${route.endPoint.addition || ''} ${route.endPoint.postalCode} ${route.endPoint.city}`
            route.addressStreet += route.endPoint.street
            route.addressPostalCode += route.endPoint.postalCode
            route.addressCity += route.endPoint.city

            if (filter(route, queries)) {
                filteredRoutes[id] = routes[id]
            }
        })

        return filteredRoutes
    },

    packages: (packages, queries, reseller) => {
        return packages.filter((packaging) => {
            packaging = structuredClone(packaging)

            packaging.branchName = reseller?.settings.branches?.find((branch) => branch.id === packaging.branchId)?.name || ''

            delete packaging._id
            delete packaging.reseller

            return filter(packaging, queries)
        })
    },

    shifts: (shifts, queries, reseller, customers, usersObject) => {
        const customerObject = customers || {}
        const users = usersObject || {}

        return shifts.filter((shift) => {
            shift = structuredClone(shift)

            const hasEndTime = !!shift.endTime

            shift.branchName = reseller?.settings.branches?.find((branch) => branch.id === shift.branchId)?.name || ''

            shift.date = moment(shift.date).format('DD-MM-YYYY')
            shift.salaryMethod = dbCodes.salaryMethod[shift.salaryMethod]
            shift.customerId = customerObject[shift.customerId]?.name || shift.customerId || ''
            shift.messengerId = users[shift.messengerId]?.name || shift.messengerId || ''
            shift.startTime = shift.startTime ? moment(shift.startTime).format('HH:mm') : ''
            shift.endTime = shift.endTime ? moment(shift.endTime).format('HH:mm') : ''
            shift.workedTime = hasEndTime ? `${shift.workedTime || 0} ${formatDuration(shift.workedTime || 0)}` : ''
            shift.pausedTime = hasEndTime ? `${shift.pausedTime || 0} ${formatDuration(shift.pausedTime || 0)}` : ''
            shift.startPoint = shift.startPoint ? `${shift.startPoint?.street || ''} ${shift.startPoint?.nr || ''}${shift.startPoint?.addtion || shift.startPoint?.addition || ''}, ${shift.startPoint?.city || ''}` : ''
            shift.endPoint = shift.endPoint ? `${shift.endPoint?.street || ''} ${shift.endPoint?.nr || ''}${shift.endPoint?.addtion || shift.endPoint?.addition || ''}, ${shift.endPoint?.city || ''}` : ''

            if (reseller) {
                shift.priceTableId = getPriceTableName(reseller.settings?.shifts?.prices, shift.priceTableId)
                shift.status = Colors.shiftStatus(reseller, shift).status
            }

            delete shift._id
            delete shift.reseller

            return filter(shift, queries)
        })
    },

    shiftTemplates: (shiftTemplates, queries, reseller) => {
        return shiftTemplates.filter((shiftTemplate) => {
            shiftTemplate = structuredClone(shiftTemplate)

            shiftTemplate.branchName = reseller?.settings.branches?.find((branch) => branch.id === shiftTemplate.branchId)?.name || ''

            shiftTemplate.date = moment(shiftTemplate.date).format('DD-MM-YYYY')

            delete shiftTemplate._id
            delete shiftTemplate.reseller

            return filter(shiftTemplate, queries)
        })
    },

    parcels: (parcels, queries, carriers, reseller) => {
        const carrierObject = {}
        carriers.map((carrier) => carrierObject[carrier.name] = carrier)

        return parcels.filter((parcel) => {
            parcel = structuredClone(parcel)

            parcel.branchName = reseller?.settings.branches?.find((branch) => branch.id === parcel.branchId)?.name || ''

            const customers = (CustomerStore.singleton && CustomerStore.state.customers) || {}
            parcel.customer = customers[parcel.customer] ? customers[parcel.customer].name : ''
            parcel.status = dbCodes.parcelStatus[parcel.status]
            parcel.invoiced = parcel.invoiced ? 'Ja' : 'Nee'
            parcel.date = moment(parcel.date).format('DD-MM-YYYY')
            parcel.address.postalCode = `${parcel.address.name} ${parcel.address.street} ${parcel.address.nr}${parcel.address.addition} ${parcel.address.postalCode} ${parcel.address.city}`

            parcel.length = (parcel.length || 0) / 10
            parcel.width = (parcel.width || 0) / 10
            parcel.height = (parcel.length || 0) / 10

            parcel.platform = carrierObject[parcel.carrier]?.platform || parcel.carrier.split('-')[1]
            parcel.carrier = carrierObject[parcel.carrier]?.displayName || parcel.carrier.split('-')[0]

            const country = _.find(Countries, (country) => country.code2 === parcel.address.country)
            if (country) {
                parcel.address.country = `${country.code2} ${country.name}`
            }

            parcel.address.isBusiness = parcel.address.isBusiness ? 'Ja' : 'Nee'

            parcel.fees.map((fee) => {
                if (typeof fee.price === 'string' && fee.price) {
                    parcel.price = formatPrice(parsePrice(parcel.price) + (parsePrice(fee.price) * fee.quantity))
                }
            })

            const productCodes = parcel.productCodes.map((item) => {
                if (item.quantity === 1) {
                    return item.productCode
                } else {
                    return `${item.quantity}x ${item.productCode}`
                }
            })

            parcel.fees.map((fee) => {
                if (fee.productCode) {
                    if (fee.quantity === 1) {
                        productCodes.push(fee.productCode)
                    } else {
                        productCodes.push(`${fee.quantity}x ${fee.productCode}`)
                    }
                }
            })

            parcel.productCodes = productCodes.join(', ')

            parcel.fees = parcel.fees.map((fee) => {
                if (fee.quantity === 1) {
                    return fee.description
                } else {
                    return `${fee.quantity}x ${fee.description}`
                }
            }).join(', ')

            parcel.plugin = parcel.plugin || parcel.webshop || ''

            delete parcel._id
            delete parcel.reseller

            return filter(parcel, queries)
        })
    },

    customers: (customers, queries, reseller) => {
        return customers.filter((customer) => {
            customer = structuredClone(customer)

            customer.branchName = reseller?.settings.branches?.find((branch) => branch.id === customer.branchId)?.name || ''

            customer.street = `${customer.address.street} ${customer.address.nr}${customer.address.addition}`
            customer.synced = customer.synced ? 'ja' : 'nee'
            customer.login = customer.login ? 'ja' : 'nee'

            if (customer.settings && customer.settings.parcels) {
                customer.settings.parcels.priceTable = customer.settings.parcels.priceTable || 'Standaard'
                customer.settings.parcels.priceTableAbroad = customer.settings.parcels.priceTableAbroad || customer.settings.parcels.priceTable
            }

            let connections = []

            Object.keys(customer.settings.webshops).map((webshop) => {
                if (customer.settings.webshops[webshop]?.active) {
                    connections.push(webshopKeyToName[webshop] || webshop)
                }
            })

            customer.connections = connections.sort().join(', ')

            delete customer._id
            delete customer.reseller

            return filter(customer, queries)
        })
    },

    addresses: (addresses, queries, reseller) => {
        return addresses.filter((address) => {
            address = structuredClone(address)

            address.branchName = reseller?.settings.branches?.find((branch) => branch.id === address.branchId)?.name || ''

            address.address = `${address.street} ${address.nr}${address.addition}`
            address.isBusiness = address.isBusiness ? 'ja' : 'nee'

            delete address._id
            delete address.reseller

            return filter(address, queries)
        })
    },

    users: (users, queries, reseller) => {
        return users.filter((user) => {
            user = structuredClone(user)

            user.branchNames = user.branchIds?.map((branchId) => reseller?.settings.branches?.find((branch) => branch.id === branchId)?.name || '').join(', ')

            user.planner = user.planner ? 'ja' : 'nee'

            delete user._id
            delete user.reseller

            return filter(user, queries)
        })
    },

    paychecks: (paychecks, queries) => {
        return paychecks.filter((paycheck) => {
            paycheck = structuredClone(paycheck)

            delete paycheck._id
            delete paycheck.reseller

            return filter(paycheck, queries)
        })
    },

    invoices: (invoices, queries, reseller) => {
        return invoices.filter((invoice) => {
            invoice = structuredClone(invoice)

            invoice.branchName = reseller?.settings.branches?.find((branch) => branch.id === invoice.branchId)?.name || ''

            const customers = (CustomerStore.singleton && CustomerStore.state.customers) || {}
            invoice.debtorCode = invoiceDebtorCode(customers[invoice.customer])
            invoice.customer = customers[invoice.customer] ? customers[invoice.customer].name : ''
            invoice.date = moment(invoice.date).format('DD-MM-YYYY')
            invoice.status = invoice.invoiced ? 'Gefactureerd Verstuurd' : 'Concept'

            delete invoice._id
            delete invoice.reseller

            return filter(invoice, queries)
        })
    },

    subscriptions: (subscriptions, queries, reseller) => {
        return subscriptions.filter((subscription) => {
            subscription = structuredClone(subscription)

            subscription.branchName = reseller?.settings.branches?.find((branch) => branch.id === subscription.branchId)?.name || ''

            const customers = (CustomerStore.singleton && CustomerStore.state.customers) || {}
            subscription.customer = customers[subscription.customer] ? customers[subscription.customer].name : ''
            subscription.date = moment(subscription.date).format('DD-MM-YYYY')
            subscription.rrule = moment(RRule.fromString(subscription.rrule).options.dtstart).format('DD-MM-YYYY')
            subscription.startDate = moment(subscription.startDate).format('DD-MM-YYYY')
            subscription.endDate = subscription.endDate === '2999-12-31' ? '' : moment(subscription.endDate).format('DD-MM-YYYY')

            delete subscription._id
            delete subscription.reseller

            return filter(subscription, queries)
        })
    },

    routeTemplates: (routeTemplates, queries, reseller, vehicles, users) => {
        return routeTemplates.filter((template) => {
            template = structuredClone(template)

            template.branchName = reseller?.settings.branches?.find((branch) => branch.id === template.branchId)?.name || ''

            const locale = getLocale()
            const language = locale === 'en' ? languages.ENGLISH : languages.DUTCH
            const text = `${RRule.fromString(template.rrule).toText((t) => {
                return language.rrule[t] || t
            }, language)}`
            template.rrule = text

            const vehicle = vehicles?.find((vehicle) => vehicle._id === template?.vehicleId)
            const vehicleType = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === template?.vehicleTypeId)
            const vehicleName = vehicle?.name || vehicleType?.name || ''
            template.vehicle = vehicleName || ''

            const messenger = users.find((user) => user._id === template.messengerId)
            template.messenger = messenger?.name

            template.startPoint = template.startPoint.name || `${template.startPoint.street} ${template.startPoint.nr}${template.startPoint.addition || ''}, ${template.startPoint.city}`
            template.endPoint = template.endPoint.name || `${template.endPoint.street} ${template.endPoint.nr}${template.endPoint.addition || ''}, ${template.endPoint.city}`

            template.optimizeForTimeslots = template.routeCalculation.optimizeForTimeslots ? 'ja' : 'nee'
            template.routeAdjustableByMessenger = template.routeCalculation.routeAdjustableByMessenger ? 'ja' : 'nee'

            const lastHistory = template.history.findLast((entry) => entry.timestamp)

            template.lastUpdated = lastHistory ? moment(lastHistory.timestamp).format('YYYY-MM-DD HH:mm') : ''

            return filter(template, queries)
        })
    },

    orderTemplates: (orderTemplates, queries, customers, routeTemplates, reseller) => {
        return orderTemplates.filter((template) => {
            template = structuredClone(template)

            template.branchName = reseller?.settings.branches?.find((branch) => branch.id === template.branchId)?.name || ''

            const locale = getLocale()
            const language = locale === 'en' ? languages.ENGLISH : languages.DUTCH
            const text = `${RRule.fromString(template.rrule).toText((t) => {
                return language.rrule[t] || t
            }, language)}`
            template.rrule = text

            template.routeTemplate = routeTemplates
                .filter((rTemplate) => rTemplate.connectedRecurringOrders.includes(template._id))
                .map((rTemplate) => rTemplate.name)
                .join(', ')

            template.customer = customers[template.customer]?.name || template.customer

            template.address = ''
            template.addresses.forEach((address) => {
                template.address += `${address.name} ${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`
            })

            return filter(template, queries)
        })
    },

    byColumns,

    objects: (objects, queries) => {
        return objects.filter((object) => {
            object = structuredClone(object)

            return filter(object, queries)
        })
    }
}

// TODO store this in a central place
const webshopKeyToName = {
    bolcom: 'Bol.com',
    lightspeed: 'Lightspeed',
    shopify: 'Shopify',
    woocommerce: 'WooCommerce',
    prestashop: 'PrestaShop',
    magento: 'Magento',
    ccvshop: 'CCV Shop',
    mijnwebwinkel: 'Mijnwebwinkel',
    shoptrader: 'Shoptrader',
    snelstart: 'SnelStart',
    exactOnline: 'Exact Online',
    shopware: 'Shopware'
}
